import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Camera, Check, ChevronLeft, ChevronRight, Loader2, MapPin, Plus, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { useCepLookup } from '../hooks/useFarms';
import { translateFarmsError } from '../lib/translateError';
import { uploadFarmLogo } from '../hooks/useFarmPhotos';
import { MACHINE_CATALOG, MATERIALS, CAPABILITIES, SPECIALIZATIONS } from '../lib/machinesCatalog';
import { validateName, validateDescription } from '@/lib/validators';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { PageShell } from '@/components/layout/PageShell';
import { cn } from '@/lib/utils';

const STEPS = ['Básico', 'Localização', 'Máquinas', 'Materiais', 'Custos & Termos'];

interface MachineEntry { brand: string; model: string; quantity: number; capabilities: string[] }

export function RegisterFarm() {
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const { tenantId } = useTenant();
  const { lookup, loading: cepLoading } = useCepLookup();

  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Passo 1
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [logoFile, setLogoFile]       = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Passo 2
  const [cep, setCep]         = useState('');
  const [city, setCity]       = useState('');
  const [stateUf, setStateUf] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat]         = useState<number | null>(null);
  const [lng, setLng]         = useState<number | null>(null);
  const [coordsWarning, setCoordsWarning] = useState(false);

  // Passo 3
  const [machines, setMachines] = useState<MachineEntry[]>([
    { brand: 'Bambu Lab', model: 'A1 mini', quantity: 1, capabilities: [] },
  ]);

  // Passo 4
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>(['PLA']);
  const [selectedSpecs, setSelectedSpecs]         = useState<string[]>([]);
  const [runs24h, setRuns24h]     = useState(false);
  const [hoursPerDay, setHoursPerDay]   = useState(8);
  const [daysPerWeek, setDaysPerWeek]   = useState(5);

  // Passo 5
  const [hourlyCost, setHourlyCost]         = useState(500);   // centavos
  const [setupFee, setSetupFee]             = useState(0);
  const [markupPct, setMarkupPct]           = useState(20);
  const [errorRatePct, setErrorRatePct]     = useState(5);
  const [energyCost, setEnergyCost]         = useState(0);     // centavos/kWh
  const [deliveryOffered, setDeliveryOffered] = useState(false);
  const [deliveryRadius, setDeliveryRadius] = useState(30);
  const [deliveryBase, setDeliveryBase]     = useState(1000);  // centavos
  const [deliveryPerKm, setDeliveryPerKm]   = useState(100);   // centavos/km
  const [minOrder, setMinOrder]             = useState(0);
  const [termsAccepted, setTermsAccepted]   = useState(false);

  async function handleCepLookup(cepVal: string) {
    const geo = await lookup(cepVal);
    if (!geo) return;
    setCity(geo.city);
    setStateUf(geo.state_uf);
    setAddress(geo.address_line);
    setLat(geo.lat);
    setLng(geo.lng);
    setCoordsWarning(!geo.lat || !geo.lng);
  }

  function toggleMaterial(m: string) {
    setSelectedMaterials((p) => p.includes(m) ? p.filter((x) => x !== m) : [...p, m]);
  }
  function toggleSpec(s: string) {
    setSelectedSpecs((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);
  }
  function toggleCap(idx: number, cap: string) {
    setMachines((p) => p.map((m, i) => i !== idx ? m : {
      ...m,
      capabilities: m.capabilities.includes(cap)
        ? m.capabilities.filter((c) => c !== cap)
        : [...m.capabilities, cap],
    }));
  }

  async function handleSubmit() {
    if (!user || !tenantId) return;
    if (!termsAccepted) { setError('Aceite os termos para continuar.'); return; }
    setSaving(true);
    setError('');
    try {
      const slug = name.toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        + '-' + Date.now();

      // 1. Farm principal
      const { data: farmData, error: farmErr } = await supabase
        .from('farms')
        .insert({
          owner_tenant_id:   tenantId,
          owner_user_id:     user.id,
          name,
          slug,
          description,
          status:            'pending_review',
          terms_accepted_at: new Date().toISOString(),
          terms_version:     '1.0',
        } as never)
        .select('id')
        .single();
      if (farmErr || !farmData) throw new Error(farmErr ? translateFarmsError(farmErr.message) : 'Erro ao criar farm.');
      const farmId = (farmData as { id: string }).id;

      // 1b. Logo (opcional)
      if (logoFile) {
        const logoUrl = await uploadFarmLogo(farmId, logoFile);
        if (logoUrl) {
          await supabase.from('farms').update({ logo_url: logoUrl } as never).eq('id', farmId);
        }
      }

      // 2. Localização (city+state obrigatórios; lat/lng opcionais)
      await supabase.from('farm_locations').insert({
        farm_id:      farmId,
        cep:          cep.replace(/\D/g, ''),
        address_line: address || null,
        city,
        state_uf:     stateUf,
        lat:          lat ?? 0,
        lng:          lng ?? 0,
      } as never);

      // 3. Máquinas
      for (const m of machines) {
        await supabase.from('farm_machines').insert({
          farm_id:      farmId,
          brand:        m.brand,
          model:        m.model,
          quantity:     m.quantity,
          capabilities: m.capabilities,
        } as never);
      }

      // 4a. Materiais (com brand vazio como padrão)
      for (const mat of selectedMaterials) {
        await supabase.from('farm_materials').insert({ farm_id: farmId, material: mat, brand: '' } as never);
      }

      // 4b. Especializações
      for (const sp of selectedSpecs) {
        await supabase.from('farm_specializations').insert({ farm_id: farmId, specialization: sp } as never);
      }

      // 4c. Turnos
      await supabase.from('farm_shifts').insert({
        farm_id:             farmId,
        runs_24h:            runs24h,
        work_hours_per_day:  runs24h ? 24 : hoursPerDay,
        work_days_per_week:  runs24h ? 7  : daysPerWeek,
      } as never);

      // 5. Custos completos
      await supabase.from('farm_costs').insert({
        farm_id:                   farmId,
        hourly_machine_cost_cents: hourlyCost,
        setup_fee_cents:           setupFee,
        filament_markup_pct:       markupPct,
        error_rate_pct:            errorRatePct,
        energy_cost_kwh_cents:     energyCost,
        delivery_offered:          deliveryOffered,
        delivery_radius_km:        deliveryOffered ? deliveryRadius : null,
        delivery_fee_base_cents:   deliveryOffered ? deliveryBase   : 0,
        delivery_fee_per_km_cents: deliveryOffered ? deliveryPerKm  : 0,
        min_order_amount_cents:    minOrder,
      } as never);

      navigate('/farms/minhas');
    } catch (e: any) {
      setError(e.message ?? 'Erro ao cadastrar farm. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  function validateStep(s: number): boolean {
    const errs: Record<string, string> = {};
    if (s === 0) {
      const nameErr = validateName(name, { label: 'Nome da farm', min: 3, max: 120 });
      const descErr = description.trim() ? validateDescription(description, 2000) : null;
      if (nameErr) errs.name = nameErr;
      if (descErr) errs.description = descErr;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const canNext = [
    name.trim().length >= 3,
    !!city && !!stateUf,
    machines.length > 0,
    selectedMaterials.length > 0,
    termsAccepted,
  ];

  return (
    <PageShell title="Cadastrar farm" back="/farms" maxWidth="md">
      {/* Progresso */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
              i < step  ? 'bg-positive text-white'
              : i === step ? 'bg-brand-700 text-white'
              : 'bg-surface-subtle text-ink-muted'
            )}>
              {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span className={cn('text-xs hidden sm:block truncate', i === step ? 'text-ink font-medium' : 'text-ink-muted')}>
              {s}
            </span>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
          </div>
        ))}
      </div>

      <div className="card p-6 space-y-4 min-h-[320px]">

        {/* ── Passo 1: Básico ─────────────────────────────────────────────── */}
        {step === 0 && (
          <>
            <h2 className="font-semibold text-ink">Informações básicas</h2>

            {/* Logo */}
            <div>
              <label className="block text-xs text-ink-muted mb-2">Logo / foto de capa (opcional)</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-surface-subtle flex items-center justify-center overflow-hidden shrink-0">
                  {logoPreview
                    ? <img src={logoPreview} alt="logo preview" className="w-full h-full object-cover" />
                    : <Camera className="w-7 h-7 text-ink-muted" />
                  }
                </div>
                <div>
                  <label className="btn-secondary cursor-pointer !py-1.5 !px-3 !text-xs gap-1.5 inline-flex items-center">
                    <Camera className="w-3.5 h-3.5" />
                    {logoPreview ? 'Trocar imagem' : 'Escolher imagem'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setLogoFile(f);
                        setLogoPreview(URL.createObjectURL(f));
                      }}
                    />
                  </label>
                  {logoPreview && (
                    <button
                      onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                      className="btn-ghost !py-1 !px-2 !text-xs text-negative ml-2"
                    >
                      Remover
                    </button>
                  )}
                  <p className="text-[11px] text-ink-muted mt-1">JPG, PNG ou WebP · máx. 5 MB</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs text-ink-muted mb-1">Nome da farm *</label>
              <input
                className={cn('input w-full', fieldErrors.name && 'border-negative')}
                value={name}
                onChange={(e) => { setName(e.target.value); if (fieldErrors.name) setFieldErrors((p) => ({ ...p, name: '' })); }}
                placeholder="ex: Farm 3D SP"
                maxLength={120}
                autoFocus
              />
              {fieldErrors.name && <p className="text-xs text-negative mt-1">{fieldErrors.name}</p>}
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Descrição</label>
              <textarea
                className={cn('input w-full h-24 resize-none', fieldErrors.description && 'border-negative')}
                value={description}
                onChange={(e) => { setDescription(e.target.value); if (fieldErrors.description) setFieldErrors((p) => ({ ...p, description: '' })); }}
                placeholder="Conte sobre sua farm, diferenciais, especialidades…"
                maxLength={2000}
              />
              {fieldErrors.description && <p className="text-xs text-negative mt-1">{fieldErrors.description}</p>}
            </div>
          </>
        )}

        {/* ── Passo 2: Localização ────────────────────────────────────────── */}
        {step === 1 && (
          <>
            <h2 className="font-semibold text-ink">Localização</h2>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-ink-muted mb-1">CEP *</label>
                <input
                  className="input w-full"
                  value={cep}
                  placeholder="00000000"
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g,'').slice(0,8);
                    setCep(v);
                    if (v.length === 8) handleCepLookup(v);
                  }}
                />
              </div>
              <div className="flex items-end">
                <button onClick={() => handleCepLookup(cep)} disabled={cepLoading} className="btn-secondary gap-2">
                  {cepLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                  Buscar
                </button>
              </div>
            </div>

            {(city || stateUf) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Cidade *</label>
                  <input className="input w-full" value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Estado *</label>
                  <input className="input w-full" value={stateUf} onChange={(e) => setStateUf(e.target.value)} maxLength={2} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-ink-muted mb-1">Endereço</label>
                  <input className="input w-full" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Rua, número…" />
                </div>
              </div>
            )}

            {/* Coordenadas manuais se geocodificação falhar */}
            {coordsWarning && (
              <div className="rounded-xl border border-warning/30 bg-warning/5 p-4 space-y-3">
                <div className="flex items-center gap-2 text-warning text-sm font-medium">
                  <AlertCircle className="w-4 h-4" />
                  Coordenadas não detectadas automaticamente
                </div>
                <p className="text-xs text-ink-muted">
                  Informe latitude e longitude manualmente para aparecer na busca por proximidade.
                  Você pode encontrar as coordenadas no Google Maps (clique com o botão direito no local).
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Latitude</label>
                    <input type="number" step="any" className="input w-full" placeholder="-23.5505"
                      value={lat ?? ''} onChange={(e) => setLat(e.target.value ? parseFloat(e.target.value) : null)} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Longitude</label>
                    <input type="number" step="any" className="input w-full" placeholder="-46.6333"
                      value={lng ?? ''} onChange={(e) => setLng(e.target.value ? parseFloat(e.target.value) : null)} />
                  </div>
                </div>
              </div>
            )}

            {!coordsWarning && lat && (
              <p className="text-xs text-positive flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Coordenadas obtidas: {lat.toFixed(4)}, {lng?.toFixed(4)}
              </p>
            )}
          </>
        )}

        {/* ── Passo 3: Máquinas ───────────────────────────────────────────── */}
        {step === 2 && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-ink">Máquinas</h2>
              <button
                onClick={() => setMachines((p) => [...p, { brand: 'Bambu Lab', model: 'A1 mini', quantity: 1, capabilities: [] }])}
                className="btn-secondary !py-1 !text-xs gap-1"
              >
                <Plus className="w-3.5 h-3.5" />Adicionar
              </button>
            </div>
            {machines.map((m, i) => (
              <div key={i} className="rounded-xl border border-border p-4 space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-ink-muted mb-1">Marca</label>
                    <select className="input w-full" value={m.brand} onChange={(e) =>
                      setMachines((p) => p.map((x, j) => j===i ? { ...x, brand: e.target.value, model: MACHINE_CATALOG.find((b)=>b.brand===e.target.value)?.models[0]??'' } : x))
                    }>
                      {MACHINE_CATALOG.map((b) => <option key={b.brand}>{b.brand}</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-ink-muted mb-1">Modelo</label>
                    <select className="input w-full" value={m.model} onChange={(e) =>
                      setMachines((p) => p.map((x, j) => j===i ? { ...x, model: e.target.value } : x))
                    }>
                      {MACHINE_CATALOG.find((b) => b.brand === m.brand)?.models.map((mod) => <option key={mod}>{mod}</option>)}
                    </select>
                  </div>
                  <div className="w-16">
                    <label className="block text-xs text-ink-muted mb-1">Qtd</label>
                    <input type="number" min={1} className="input w-full" value={m.quantity}
                      onChange={(e) => setMachines((p) => p.map((x, j) => j===i ? { ...x, quantity: Number(e.target.value) } : x))} />
                  </div>
                  {machines.length > 1 && (
                    <div className="flex items-end">
                      <button onClick={() => setMachines((p) => p.filter((_, j)=>j!==i))} className="btn-ghost !p-1.5 text-negative">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs text-ink-muted mb-1">Capacidades</p>
                  <div className="flex flex-wrap gap-1">
                    {CAPABILITIES.map((c) => (
                      <button key={c.value} onClick={() => toggleCap(i, c.value)}
                        className={cn('px-2 py-0.5 rounded-full text-xs transition-colors',
                          m.capabilities.includes(c.value) ? 'bg-brand-100 text-brand-800' : 'bg-surface-subtle text-ink-muted')}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── Passo 4: Materiais + Especializações + Turnos ───────────────── */}
        {step === 3 && (
          <>
            <h2 className="font-semibold text-ink">Materiais que você imprime *</h2>
            <div className="flex flex-wrap gap-2">
              {MATERIALS.map((m) => (
                <button key={m.value} onClick={() => toggleMaterial(m.value)}
                  className={cn('px-3 py-1.5 rounded-xl text-sm transition-colors',
                    selectedMaterials.includes(m.value) ? 'bg-brand-100 text-brand-800 font-medium' : 'bg-surface-subtle text-ink-muted')}>
                  {m.label}
                </button>
              ))}
            </div>

            <h2 className="font-semibold text-ink mt-2">Especializações</h2>
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS.map((s) => (
                <button key={s.value} onClick={() => toggleSpec(s.value)}
                  className={cn('px-3 py-1.5 rounded-xl text-sm transition-colors',
                    selectedSpecs.includes(s.value) ? 'bg-brand-100 text-brand-800 font-medium' : 'bg-surface-subtle text-ink-muted')}>
                  {s.label}
                </button>
              ))}
            </div>

            <h2 className="font-semibold text-ink mt-2">Disponibilidade</h2>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-ink-muted mb-1">Funciona 24h?</label>
                <select className="input w-full" value={runs24h ? '1' : '0'} onChange={(e) => setRuns24h(e.target.value==='1')}>
                  <option value="0">Não</option>
                  <option value="1">Sim</option>
                </select>
              </div>
              {!runs24h && (
                <>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Horas/dia</label>
                    <input type="number" min={1} max={24} className="input w-full" value={hoursPerDay}
                      onChange={(e) => setHoursPerDay(Number(e.target.value))} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Dias/semana</label>
                    <input type="number" min={1} max={7} className="input w-full" value={daysPerWeek}
                      onChange={(e) => setDaysPerWeek(Number(e.target.value))} />
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ── Passo 5: Custos & Termos ────────────────────────────────────── */}
        {step === 4 && (
          <>
            <h2 className="font-semibold text-ink">Precificação</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-ink-muted mb-1">Custo/hora máquina (R$)</label>
                <CurrencyInput className="input w-full" value={hourlyCost} onChange={setHourlyCost} />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Taxa de setup (R$)</label>
                <CurrencyInput className="input w-full" value={setupFee} onChange={setSetupFee} />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Markup filamento (%)</label>
                <input type="number" min={0} max={500} className="input w-full"
                  value={markupPct} onChange={(e) => setMarkupPct(Number(e.target.value))} />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Taxa de erro/falha (%)</label>
                <input type="number" min={0} max={100} className="input w-full"
                  value={errorRatePct} onChange={(e) => setErrorRatePct(Number(e.target.value))} />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Energia (R$/kWh)</label>
                <CurrencyInput className="input w-full" value={energyCost} onChange={setEnergyCost} />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Pedido mínimo (R$)</label>
                <CurrencyInput className="input w-full" value={minOrder} onChange={setMinOrder} />
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input type="checkbox" checked={deliveryOffered} onChange={(e) => setDeliveryOffered(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm font-medium text-ink">Oferecer entrega/frete</span>
              </label>
              {deliveryOffered && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Raio máximo (km)</label>
                    <input type="number" min={1} className="input w-full"
                      value={deliveryRadius} onChange={(e) => setDeliveryRadius(Number(e.target.value))} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Taxa base (R$)</label>
                    <CurrencyInput className="input w-full" value={deliveryBase} onChange={setDeliveryBase} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-muted mb-1">Por km (R$)</label>
                    <CurrencyInput className="input w-full" value={deliveryPerKm} onChange={setDeliveryPerKm} />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-warning/30 bg-warning/5 p-3 text-xs text-warning">
              A plataforma cobra <strong>7%</strong> sobre o valor cobrado do cliente.
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="mt-0.5 w-4 h-4" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
              <span className="text-sm text-ink-muted">
                Li e aceito os{' '}
                <a href="/farms/termos" target="_blank" className="text-brand-700 underline">Termos de Uso da MYND Farms</a>.
              </span>
            </label>

            {error && <p className="text-sm text-negative rounded-xl bg-negative/5 border border-negative/20 p-3">{error}</p>}
          </>
        )}
      </div>

      {/* Navegação */}
      <div className="flex justify-between mt-4">
        <button onClick={() => setStep((s) => Math.max(0, s-1))} disabled={step===0} className="btn-secondary gap-2">
          <ChevronLeft className="w-4 h-4" />Anterior
        </button>
        {step < STEPS.length - 1 ? (
          <button
            onClick={() => { if (validateStep(step)) setStep((s) => s + 1); }}
            disabled={!canNext[step]}
            className="btn-primary gap-2"
          >
            Próximo<ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={saving || !termsAccepted} className="btn-primary gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Cadastrar farm
          </button>
        )}
      </div>
    </PageShell>
  );
}
